import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import { getCharacterById, updateCharacter, uploadCharacterImage } from '../../services/characterService';
import { useAuth } from '../../context/AuthContext';
import { Box, Typography, CircularProgress, Paper, Grid, Button, Container, Alert, Avatar } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch'; // Added for D&D Beyond link

// Helper function to format Firestore Timestamps
const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  let date;
  if (timestamp.seconds) { // Firebase Timestamp object
    date = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
  } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    date = new Date(timestamp);
  } else {
    return 'Invalid Date Input';
  }
  return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

// Helper function to format class display (can be shared or defined locally)
const formatCharacterClassesForDetail = (classesArray, totalLevelNum, raceName) => {
  if (!classesArray || classesArray.length === 0) {
    return `Level ${totalLevelNum || 'N/A'} ${raceName || ''}`.trim();
  }
  const classDetails = classesArray.map(c => `${c.className} ${c.classLevel}`).join(' / ');
  return `Level ${totalLevelNum || 'N/A'} ${raceName || ''} - ${classDetails}`;
};

const CharacterDetailPage = ({ characters, loadingCharacters: initialLoading }) => {
  const { characterId } = useParams();
  const { currentUser } = useAuth(); // Get current user
  const navigate = useNavigate();
  const [character, setCharacter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditingImage, setIsEditingImage] = useState(false);
  const [newImageFile, setNewImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    const fetchCharacter = async () => {
      setLoading(true);
      setError('');

      // Attempt to find character in the passed list first
      if (!initialLoading && characters && characters.length > 0) {
        const foundCharacter = characters.find(char => char.id === characterId);
        if (foundCharacter) {
          setCharacter(foundCharacter);
          setLoading(false);
          return;
        }
      }

      // If not found in props or props are loading, fetch from DB
      try {
        const charData = await getCharacterById(characterId);
        if (charData) {
          setCharacter(charData);
        } else {
          setError('Character not found.');
        }
      } catch (err) {
        console.error("Error fetching character details:", err);
        setError('Failed to load character details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (characterId) {
      fetchCharacter();
    }
  }, [characterId, characters, initialLoading]);

  const handleImageFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file.');
        setNewImageFile(null);
        setImagePreview('');
        return;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Image size should not exceed 5MB.');
        setNewImageFile(null);
        setImagePreview('');
        return;
      }
      setNewImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setError('');
    } else {
      setNewImageFile(null);
      setImagePreview('');
    }
  };

  const handleImageUpload = async () => {
    if (!newImageFile || !currentUser || !characterId) {
      setError("No image selected or user/character not identified.");
      return;
    }
    setUploadingImage(true);
    setError('');
    try {
      // Corrected: uploadCharacterImage expects characterId and file
      const imageUrl = await uploadCharacterImage(characterId, newImageFile);
      // Corrected: updateCharacter expects characterId, updates object. userId is not needed here.
      await updateCharacter(characterId, { imageUrl }); 
      setCharacter(prev => ({ ...prev, imageUrl }));
      setIsEditingImage(false);
      setNewImageFile(null);
      if (imagePreview) URL.revokeObjectURL(imagePreview);
      setImagePreview('');
    } catch (err) {
      console.error("Error uploading new character image:", err);
      setError(err.message || "Failed to upload image. Please try again.");
    } finally {
      setUploadingImage(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 64px)', bgcolor: '#1c1c1c' }}>
        <CircularProgress sx={{ color: '#d98d2a' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container component="main" maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3, bgcolor: '#444444', color: '#F5F5F5', border: '1px solid #d98d2a' }}>
          <Alert severity="error" sx={{ bgcolor: '#ff6b6b', color: 'white' }}>{error}</Alert>
          <Button component={RouterLink} to="/my-characters" variant="outlined" sx={{ mt: 2, color: '#d98d2a', borderColor: '#d98d2a', '&:hover': { borderColor: '#c87d26', backgroundColor: 'rgba(217, 141, 42, 0.1)'} }}>
            Back to My Characters
          </Button>
        </Paper>
      </Container>
    );
  }

  if (!character) {
    return (
      <Container component="main" maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3, bgcolor: '#444444', color: '#F5F5F5', border: '1px solid #d98d2a' }}>
          <Typography sx={{fontFamily: 'Georgia, serif'}}>Character data is not available.</Typography>
          <Button component={RouterLink} to="/my-characters" variant="outlined" sx={{ mt: 2, color: '#d98d2a', borderColor: '#d98d2a', '&:hover': { borderColor: '#c87d26', backgroundColor: 'rgba(217, 141, 42, 0.1)'} }}>
            Back to My Characters
          </Button>
        </Paper>
      </Container>
    );
  }

  // Check if the current user is the owner of the character
  const isOwner = currentUser && character && character.userId === currentUser.uid;

  return (
    <Container component="main" maxWidth="lg" sx={{ mt: 4, mb: 4, bgcolor: '#1c1c1c' }}>
      <Paper elevation={3} sx={{ 
        p: { xs: 2, md: 4 }, 
        bgcolor: '#f0e6d2',
        color: '#5c3c10',
        border: '1px solid #8d6e63',
        fontFamily: 'Georgia, serif' 
      }}>
        {error && <Alert severity="error" sx={{ mb: 2, bgcolor: '#ff6b6b', color: 'white' }}>{error}</Alert>}
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
            <Avatar 
              src={imagePreview || character.imageUrl || undefined} // Show preview if available, then character.imageUrl
              alt={character.name}
              sx={{
                width: { xs: 150, md: 200 }, 
                height: { xs: 150, md: 200 }, 
                margin: 'auto',
                mb: 2,
                border: '3px solid #8d6e63',
                fontSize: '4rem' // Fallback for text avatar if no image
              }}
            >
              {character.name ? character.name.charAt(0).toUpperCase() : 'C'}
            </Avatar>
            {isOwner && (
              <Box>
                {!isEditingImage ? (
                  <Button 
                    variant="outlined" 
                    onClick={() => setIsEditingImage(true)} 
                    sx={{fontFamily: 'inherit', color: '#5c3c10', borderColor: '#8d6e63', '&:hover': {borderColor: '#5c3c10', backgroundColor: 'rgba(92,60,16,0.1)'}}}>
                    Change Image
                  </Button>
                ) : (
                  <Box sx={{mt:1}}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="character-detail-image-upload"
                      type="file"
                      onChange={handleImageFileChange}
                    />
                    <label htmlFor="character-detail-image-upload">
                      <Button variant="contained" component="span" fullWidth sx={{
                        fontFamily: 'inherit',
                        backgroundColor: '#d98d2a',
                        color: '#FFFFFF',
                        '&:hover': { backgroundColor: '#b46d1f' },
                        mb: 1
                      }}>
                        Choose File
                      </Button>
                    </label>
                    {newImageFile && (
                      <Button 
                        variant="contained" 
                        onClick={handleImageUpload} 
                        disabled={uploadingImage}
                        fullWidth
                        sx={{
                          fontFamily: 'inherit',
                          backgroundColor: '#5c3c10',
                          color: '#FFFFFF',
                          '&:hover': { backgroundColor: '#4a300d' },
                          mb:1
                        }}
                      >
                        {uploadingImage ? <CircularProgress size={24} color="inherit" /> : 'Upload New Image'}
                      </Button>
                    )}
                    <Button 
                      variant="outlined" 
                      onClick={() => {
                        setIsEditingImage(false); 
                        setNewImageFile(null); 
                        if(imagePreview) URL.revokeObjectURL(imagePreview);
                        setImagePreview('');
                        setError('');
                      }} 
                      fullWidth
                      sx={{fontFamily: 'inherit', color: '#5c3c10', borderColor: '#8d6e63', '&:hover': {borderColor: '#5c3c10', backgroundColor: 'rgba(92,60,16,0.1)'}}}>
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography variant="h3" component="h1" sx={{ 
              color: '#3e2723',
              mb: 1, 
              textAlign: { xs: 'center', md: 'left' }, 
              fontFamily: 'inherit' 
            }}>
              {character.name}
            </Typography>
            <Typography variant="h6" sx={{ 
              color: '#6a4f4b',
              mb: 3, 
              textAlign: { xs: 'center', md: 'left' }, 
              fontStyle: 'italic', 
              fontFamily: 'inherit' 
            }}>
              {formatCharacterClassesForDetail(character.classes, character.totalLevel, character.race)}
            </Typography>
            <Typography variant="h5" sx={{ color: '#4e342e', mb: 1, fontFamily: 'inherit' }}>Details:</Typography>
            <Typography sx={{fontFamily: 'inherit'}}><strong>Created:</strong> {formatDate(character.createdAt)}</Typography>
            <Typography sx={{fontFamily: 'inherit'}}><strong>Last Updated:</strong> {formatDate(character.updatedAt)}</Typography>
            {character.alignment && <Typography sx={{fontFamily: 'inherit'}}><strong>Alignment:</strong> {character.alignment}</Typography>}
            {character.background && <Typography sx={{fontFamily: 'inherit'}}><strong>Background:</strong> {character.background}</Typography>}
            
            <Typography sx={{fontFamily: 'inherit'}}><strong>Race:</strong> {character.race}</Typography>
            <Typography sx={{fontFamily: 'inherit'}}><strong>Total Level:</strong> {character.totalLevel}</Typography>            {character.classes && character.classes.length > 0 && (
                <Box sx={{mt: 1, mb:1}}>
                    <Typography sx={{fontFamily: 'inherit'}}><strong>Classes:</strong></Typography>
                    {character.classes.map((cls, index) => (
                        <Typography key={index} sx={{fontFamily: 'inherit', ml: 2}}>
                            - {cls.className}: Level {cls.classLevel}
                        </Typography>
                    ))}
                </Box>
            )}

            {character.dndBeyondLink && (
              <Box sx={{ mt: 2, mb: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<LaunchIcon />}
                  href={character.dndBeyondLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: '#6a4f4b',
                    borderColor: '#6a4f4b',
                    fontFamily: 'inherit',
                    '&:hover': {
                      backgroundColor: 'rgba(106, 79, 75, 0.1)',
                      borderColor: '#4e342e'
                    }
                  }}
                >
                  View on D&D Beyond
                </Button>
              </Box>
            )}

            {character.xp && <Typography sx={{fontFamily: 'inherit'}}><strong>XP:</strong> {character.xp}</Typography>}
            {character.hp && <Typography sx={{fontFamily: 'inherit'}}><strong>HP:</strong> {character.hp.current}/{character.hp.max}</Typography>}
            {character.armorClass && <Typography sx={{fontFamily: 'inherit'}}><strong>Armor Class:</strong> {character.armorClass}</Typography>}
            {character.speed && <Typography sx={{fontFamily: 'inherit'}}><strong>Speed:</strong> {character.speed} ft.</Typography>}
          </Grid>
        </Grid>
        
        {character.attributes && Object.keys(character.attributes).length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" sx={{ color: '#4e342e', mb: 2, fontFamily: 'inherit' }}>Attributes</Typography>
            <Grid container spacing={2}>
              {Object.entries(character.attributes).map(([key, value]) => (
                <Grid item xs={6} sm={4} md={3} key={key}>
                  <Paper sx={{ p: 2, bgcolor: '#fff3e0', border: '1px solid #e0a800', borderRadius: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium', color: '#e65100' }}>{key}</Typography>
                    <Typography sx={{ color: '#3e2723' }}>{value}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {character.story && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" sx={{ color: '#4e342e', mb: 1, fontFamily: 'inherit' }}>Backstory</Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', lineHeight: 1.6, bgcolor: '#faf3e0', p:2, borderRadius:1, border: '1px solid #e0a800' }}>
              {character.story}
            </Typography>
          </Box>
        )}

        {character.notes && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" sx={{ color: '#4e342e', mb: 1, fontFamily: 'inherit' }}>Notes</Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', lineHeight: 1.6, bgcolor: '#faf3e0', p:2, borderRadius:1, border: '1px solid #e0a800' }}>
              {character.notes}
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button component={RouterLink} to="/my-characters" variant="contained" sx={{
            fontFamily: 'inherit',
            backgroundColor: '#d98d2a',
            color: '#FFFFFF', 
            '&:hover': { backgroundColor: '#b46d1f' }, 
            mr: isOwner ? 1 : 0 // Add margin if edit button might appear
          }}>
            Return to My Characters
          </Button>
          {/* Basic Edit Button - Future Enhancement: Link to a full edit page or modal */}
          {isOwner && (
            <Button 
              variant="outlined"
              onClick={() => navigate(`/character/edit/${characterId}`)} // Assuming an edit route like this
              sx={{
                fontFamily: 'inherit',
                color: '#5c3c10', 
                borderColor: '#8d6e63', 
                '&:hover': {borderColor: '#5c3c10', backgroundColor: 'rgba(92,60,16,0.1)'}
              }}
            >
              Edit Character
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default CharacterDetailPage;
